import { Box, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { colorTokens, radiusTokens, shadowTokens, spacingTokens, typographyTokens } from '../app/designTokens';

const { semantic } = colorTokens;

const VARIANT_STYLES = {
  Default: {
    bgcolor: semantic.bg.surface,
    border: `1px solid ${semantic.border.subtle}`,
    boxShadow: shadowTokens.none,
  },
  Elevated: {
    bgcolor: semantic.bg.surface,
    border: `1px solid ${semantic.border.subtle}`,
    boxShadow: shadowTokens.sm,
  },
  Interactive: {
    bgcolor: semantic.bg.surface,
    border: `1px solid ${semantic.border.subtle}`,
    boxShadow: shadowTokens.none,
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: semantic.border.default,
      bgcolor: semantic.action.secondary.bg,
    },
  },
  Selected: {
    bgcolor: semantic.bg.surfaceSelected,
    border: `1px solid ${semantic.status.brand.border}`,
    boxShadow: shadowTokens.none,
  },
  Warning: {
    bgcolor: alpha(semantic.warning, 0.08),
    border: `1px solid ${semantic.warning}`,
    boxShadow: shadowTokens.none,
  },
  Danger: {
    bgcolor: alpha(semantic.error, 0.08),
    border: `1px solid ${semantic.error}`,
    boxShadow: shadowTokens.none,
  },
};

/**
 * Shared Surface / Card wrapper (SectionCard).
 * @param {'Default'|'Elevated'|'Interactive'|'Selected'|'Warning'|'Danger'} [variant='Default']
 */
export function SectionCard({
  title,
  subtitle,
  actions,
  children,
  noPadding = false,
  variant = 'Default',
  onClick,
  sx,
}) {
  const variantSx = VARIANT_STYLES[variant] || VARIANT_STYLES.Default;
  const interactive = Boolean(onClick) || variant === 'Interactive';
  const pad = spacingTokens.cardPadding / spacingTokens.unit;

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive && onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(event);
              }
            }
          : undefined
      }
      sx={{
        mb: spacingTokens.sectionGap / spacingTokens.unit,
        borderRadius: `${radiusTokens.card}px`,
        backgroundImage: 'none',
        overflow: 'hidden',
        ...variantSx,
        ...sx,
      }}
    >
      {(title || actions) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            // Keep title/actions padded even when body uses noPadding (embedded tables).
            px: pad,
            pt: pad,
            pb: title || subtitle ? 1.5 : 0,
          }}
        >
          <Box>
            {title && (
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontFamily: typographyTokens.fontFamily,
                  fontWeight: typographyTokens.fontWeightSemibold,
                  fontSize: 14,
                  lineHeight: '28px',
                  color: semantic.text.primary,
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="body2"
                sx={{ color: semantic.text.secondary, fontSize: 13 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {actions}
        </Box>
      )}
      <Box
        sx={{
          p: noPadding ? 0 : pad,
          pt: noPadding ? 0 : (title || subtitle ? 1 : pad),
        }}
      >
        {children}
      </Box>
    </Paper>
  );
}
